import { t } from "i18next";
import moment from "moment";
import { v4 as uuidv4 } from "uuid";

import { OBJECT_TYPES_SCHEDULE_ITEM } from "../constants/fast";

import type {
  ScheduleItem,
  ScheduleItemWithScheduleId,
} from "../api/FastAPI.types";

export interface ClusterItem extends Omit<
  ScheduleItem,
  "object_type" | "object_format" | "object_uuid" | "user_uuid"
> {
  object_type: typeof OBJECT_TYPES_SCHEDULE_ITEM.CLUSTER;
  fast_channel_timeslot_uuid?: string;
  fast_channel_timeslot_name?: string;
  fast_channel_timeslot_color?: string;
  fast_channel_schedule_uuid: string;
  itemIdList: Set<string>;
  user_uuid?: string;
  isOnBaseline: boolean;
}

export interface TimelineMediaInfo {
  isFirstVariantMedia: boolean | null;
  variantMediaList: ScheduleItemWithScheduleId[];
  isOnBaseline: boolean;
}

export type ScheduleItemsWithVariantInfo = ScheduleItemWithScheduleId &
  TimelineMediaInfo;

export type ScheduleItemsWithClusters = ScheduleItemsWithVariantInfo[] &
  ClusterItem[];

interface ClusteringList {
  itemList: ScheduleItemsWithClusters;
  currentCluster?: ClusterItem;
}

const areItemsAdjacent = (
  item: ScheduleItemWithScheduleId,
  nextItem?: ScheduleItemWithScheduleId,
): boolean => {
  if (!nextItem) {
    return false;
  }

  const itemEnd = new Date(item.end_time).valueOf();
  const nextItemStart = new Date(nextItem.start_time).valueOf();

  return itemEnd === nextItemStart - 1;
};

const getIsOnBaseline = (
  item: ScheduleItemWithScheduleId,
  defaultScheduleId?: string,
): boolean => item.fast_channel_schedule_uuid === defaultScheduleId;

const getCanItemBeClustered = (
  item: ScheduleItemsWithVariantInfo,
  nextItem?: ScheduleItemsWithVariantInfo,
): boolean =>
  Boolean(
    nextItem?.variantMediaList.length === 0 &&
    item.variantMediaList.length === 0 &&
    item.fast_channel_timeslot_uuid === nextItem?.fast_channel_timeslot_uuid &&
    item.isOnBaseline &&
    nextItem?.isOnBaseline,
  );

const getNewCluster = (
  newItem: ScheduleItemWithScheduleId | ClusterItem,
  currentCluster?: ClusterItem,
): ClusterItem => {
  const nextItemIsCluster = "itemIdList" in newItem;

  const nextItemIdList = nextItemIsCluster
    ? newItem.itemIdList
    : [newItem.uuid];

  const itemIdList = new Set([
    ...(currentCluster?.itemIdList ?? []),
    ...nextItemIdList,
  ]);

  const updatedAt =
    currentCluster &&
    moment(currentCluster.updated_at).isAfter(moment(newItem.updated_at))
      ? currentCluster.updated_at
      : newItem.updated_at;

  const hasSameUserId = currentCluster?.user_uuid === newItem.user_uuid;

  const userId =
    hasSameUserId || !currentCluster ? newItem.user_uuid : undefined;

  return {
    uuid: currentCluster?.uuid ?? uuidv4(),
    object_label: t("clusterTitle", {
      ns: "editor-fast-channel",
      count: itemIdList.size,
    }),
    fast_channel_schedule_uuid: newItem.fast_channel_schedule_uuid,
    object_total_duration:
      (currentCluster?.object_total_duration ?? 0) +
      newItem.object_total_duration,
    object_duration:
      (currentCluster?.object_duration ?? 0) + newItem.object_duration,
    object_type: OBJECT_TYPES_SCHEDULE_ITEM.CLUSTER,
    object_picture: null,
    start_time: currentCluster?.start_time ?? newItem.start_time,
    end_time: newItem.end_time,
    fast_channel_timeslot_uuid:
      currentCluster?.fast_channel_timeslot_uuid ??
      newItem.fast_channel_timeslot_uuid,
    fast_channel_timeslot_name:
      currentCluster?.fast_channel_timeslot_name ??
      newItem.fast_channel_timeslot_name,
    fast_channel_timeslot_color:
      currentCluster?.fast_channel_timeslot_color ??
      newItem.fast_channel_timeslot_color,
    itemIdList,
    interstitial_adbreak_config: {
      adbreaks_at: null,
      cuepoints: null,
    },
    interstitial_adbreak_uuid: null,
    updated_at: updatedAt,
    user_uuid: userId,
    isOnBaseline: true,
  };
};

const getTimelineMediaInfo = (
  media: ScheduleItemWithScheduleId,
  isOnBaseline: boolean,
  scheduleItemList: ScheduleItemWithScheduleId[],
): Pick<TimelineMediaInfo, "isFirstVariantMedia" | "variantMediaList"> =>
  scheduleItemList.reduce(
    (
      acc: Pick<TimelineMediaInfo, "isFirstVariantMedia" | "variantMediaList">,
      mediaToCompare,
    ) => {
      const haveSameStartTime = media.start_time === mediaToCompare.start_time;
      const haveSameMediaId = media.uuid === mediaToCompare.uuid;

      if (!haveSameMediaId && haveSameStartTime) {
        if (isOnBaseline) {
          acc.variantMediaList.push(mediaToCompare);
          acc.isFirstVariantMedia = false;
        } else {
          acc.variantMediaList = [];
          acc.isFirstVariantMedia = true;
        }
      }

      return acc;
    },
    {
      isFirstVariantMedia: false,
      variantMediaList: [],
    },
  );

const MIN_CLUSTER_HOUR_PERIOD = 12;

export const getIsClustered = (
  timelinePeriodInMillisecondes: number,
): boolean => {
  const periodInHours = timelinePeriodInMillisecondes / 1000 / 3600;

  return periodInHours >= MIN_CLUSTER_HOUR_PERIOD;
};

const mergeTimelineData = ({
  data,
  timelinePeriod,
  defaultScheduleId,
}: {
  data?: ScheduleItemWithScheduleId[];
  timelinePeriod?: number;
  defaultScheduleId?: string;
}): ScheduleItemsWithVariantInfo[] | ScheduleItemsWithClusters | null => {
  if (!data || !timelinePeriod || timelinePeriod < 0 || !defaultScheduleId)
    return null;

  const itemsWithInfo: ScheduleItemsWithVariantInfo[] = data.map(
    (item, _, array) => {
      const isOnBaseline = getIsOnBaseline(item, defaultScheduleId);

      const { isFirstVariantMedia, variantMediaList } = getTimelineMediaInfo(
        item,
        isOnBaseline,
        array,
      );

      return {
        ...item,
        isOnBaseline,
        variantMediaList,
        isFirstVariantMedia,
      };
    },
  );

  const canClustered = getIsClustered(timelinePeriod);

  if (!canClustered) return itemsWithInfo;

  // Reduce data to create clusters
  const clusterizedList = itemsWithInfo.reduce(
    (acc: ClusteringList, currentItem, currentIndex, array) => {
      const nextItem = array[currentIndex + 1];
      const { currentCluster } = acc;

      const canItemsBeClustered = getCanItemBeClustered(currentItem, nextItem);

      const shouldCluster =
        canItemsBeClustered && areItemsAdjacent(currentItem, nextItem);

      const isLastClusterItem =
        (!nextItem || !shouldCluster) && !!currentCluster;

      if (shouldCluster || isLastClusterItem) {
        const newCluster = getNewCluster(currentItem, currentCluster);

        acc.currentCluster = isLastClusterItem ? undefined : newCluster;

        if (isLastClusterItem) {
          acc.itemList.push(newCluster);
        }
      } else {
        acc.itemList.push(currentItem);
      }

      return acc;
    },
    { currentCluster: undefined, itemList: [] },
  ).itemList;

  // Clusterize clusters if they are next to each other
  const superClusterizedList = clusterizedList.reduce(
    (acc: ClusteringList, currentItem, currentIndex, array) => {
      const nextItem = array[currentIndex + 1];

      const haveSameTimeslot =
        currentItem.fast_channel_timeslot_uuid ===
        nextItem?.fast_channel_timeslot_uuid;

      const canItemsBeClustered =
        currentItem.object_type === OBJECT_TYPES_SCHEDULE_ITEM.CLUSTER &&
        nextItem?.object_type === OBJECT_TYPES_SCHEDULE_ITEM.CLUSTER &&
        haveSameTimeslot;

      const shouldCluster =
        canItemsBeClustered && areItemsAdjacent(currentItem, nextItem);

      const isLastClusterItem =
        (!nextItem || !shouldCluster) && !!acc.currentCluster;

      if (shouldCluster || isLastClusterItem) {
        const newCluster = getNewCluster(currentItem, acc.currentCluster);

        acc.currentCluster = isLastClusterItem ? undefined : newCluster;

        if (isLastClusterItem) {
          acc.itemList.push(newCluster);
        }
      } else {
        acc.itemList.push(currentItem);
      }

      return acc;
    },
    { currentCluster: undefined, itemList: [] },
  ).itemList;

  return superClusterizedList;
};

export default mergeTimelineData;
